/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { Fragment } from "react"

import type { ApkDetail, ApkFlagSource } from "../../features/apk-detail/apk-detail-types"
import { Badge } from "../primitives/badge"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"

import styles from "./apk-categories.module.css"

interface Props {
    /**
     * APK classifications from each source.
     *
     * Any group can be empty when a source has no classifications for the APK.
     */
    apk: Pick<ApkDetail, "malwareTypes" | "malwareFamilies" | "apkFlags">
}

/**
 * Display label for each flag source.
 *
 * Every source is rendered as a row, so adding a source requires adding its
 * label here rather than silently omitting its flags.
 */
const FlagSourceLabel = {
    KOODOUS: "Koodous Tags"
} as const satisfies Record<ApkFlagSource, string>

/**
 * Displays the APK's classifications grouped by source.
 *
 * Malware types use filled badges and families use outlined badges, matching
 * the device malware tab. Flags from outside sources, such as Koodous tags, get
 * one row per source and are shown unchanged from the API.
 *
 * Each group remains visible when empty and shows a dash instead of being
 * omitted, making it clear that no classifications were found.
 *
 * @example
 * <ApkCategories apk={apk} />
 */
export const ApkCategories = observer(({ apk }: Props) => (
    <Card>
        <CardHeader>
            <CardTitle>
                <h3 className="m-0 text-xl font-bold uppercase">Categories</h3>
            </CardTitle>
        </CardHeader>
        <CardContent>
            <dl className={styles.table}>
                <dt className={styles.key}>Malware Types:</dt>
                <dd className={styles.value}>{renderBadges(apk.malwareTypes, "secondary")}</dd>

                <dt className={styles.key}>Malware Families:</dt>
                <dd className={styles.value}>{renderBadges(apk.malwareFamilies, "outline")}</dd>

                {Object.entries(FlagSourceLabel).map(([source, label]) => (
                    <Fragment key={source}>
                        <dt className={styles.key}>{label}:</dt>
                        <dd className={styles.value}>
                            {renderBadges(
                                apk.apkFlags.filter((flag) => flag.source === source).map((flag) => flag.name),
                                "outline"
                            )}
                        </dd>
                    </Fragment>
                ))}
            </dl>
        </CardContent>
    </Card>
))

/**
 * Renders a group's names as wrapping badges.
 *
 * @param names - Names in the group.
 * @param variant - Badge variant used for the group.
 * @returns The badges, or a dash when the group is empty.
 */
function renderBadges(names: string[], variant: "secondary" | "outline") {
    if (names.length === 0) return "—"

    return (
        <span className="flex flex-wrap gap-1">
            {names.map((name) => (
                <Badge key={name} variant={variant}>
                    {name}
                </Badge>
            ))}
        </span>
    )
}
